import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { styled } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDispatch, useSelector } from "react-redux";
import { fetchDesignationBased } from "../../action/DesignationBasedAction";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

// ─── Color helpers ────────────────────────────────────────────────────────────
const rateColor = (pct) =>
  pct >= 80 ? "#16a34a" : pct >= 60 ? "#d97706" : "#dc2626";
const rateBg = (pct) =>
  pct >= 80 ? "#dcfce7" : pct >= 60 ? "#fef3c7" : "#fee2e2";

// ─── Styled shells ────────────────────────────────────────────────────────────
const Page = styled(Box)(() => ({
  width: "100%",
  padding: "0 0 80px",
}));

const Card = styled(Box)({
  background: "#fff",
  border: "0.5px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
});

const SectionLabel = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: 6,
  marginTop: 14,
});

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, color }) {
  return (
    <Box
      sx={{
        flex: "1 1 0",
        background: "#fff",
        border: "0.5px solid #e2e8f0",
        borderRadius: "12px",
        p: "10px 12px",
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ─── Percentage badge ─────────────────────────────────────────────────────────
function PctBadge({ pct }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: "7px",
        py: "1px",
        borderRadius: "10px",
        fontSize: 11,
        fontWeight: 600,
        background: rateBg(pct),
        color: rateColor(pct),
      }}
    >
      {pct}%
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function DesignationBased() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { loading, data, error } = useSelector((s) => s.designationBased);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const formatDate = (d) => dayjs(d).format("YYYY-MM-DD");
  const formatTime = (d) =>
    new Date(d).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const load = (date) => dispatch(fetchDesignationBased(formatDate(date)));

  useEffect(() => {
    load(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    load(date);
  };

  // ─── Derived totals ──────────────────────────────────────────────────────────
  const sortedData = useMemo(
    () =>
      Array.isArray(data)
        ? [...data].sort((a, b) =>
            Number(b.Strength || 0) - Number(a.Strength || 0)
          )
        : [],
    [data]
  );

  const totalStrength = useMemo(
    () => sortedData.reduce((s, i) => s + Number(i.Strength || 0), 0),
    [sortedData]
  );
  const totalAttendance = useMemo(
    () => sortedData.reduce((s, i) => s + Number(i.Attendance || 0), 0),
    [sortedData]
  );
  const totalAbsent = totalStrength - totalAttendance;
  const totalPercentage =
    totalStrength > 0
      ? Math.round((totalAttendance / totalStrength) * 100)
      : 0;

  // ─── PDF Export ──────────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const at = new Date();
    const pw = doc.internal.pageSize.getWidth();

    doc.setFontSize(22).setFont("helvetica", "bold").setTextColor(0, 74, 173);
    doc.text("DTS Biztrack", pw / 2, 20, { align: "center" });
    doc.setFontSize(13).setTextColor(100, 116, 139);
    doc.text(
      `Designation Based Attendance – ${formatDate(selectedDate)}`,
      pw / 2,
      30,
      { align: "center" }
    );
    doc.setDrawColor(0, 74, 173).setLineWidth(0.5).line(15, 36, pw - 15, 36);

    const pairs = [
      ["Date – ", formatDate(selectedDate)],
      ["Time – ", formatTime(at)],
      ["Strength –  ", totalStrength.toString()],
      ["Attendance –  ", `${totalAttendance} (${totalPercentage}%)`],
    ];
    const sx = 15, tw = pw - 30;
    pairs.forEach(([bold, val], i) => {
      const col = sx + tw * (0.125 + i * 0.25);
      const fw = doc.getTextWidth(bold + val);
      doc.setFontSize(10).setFont("helvetica", "bold").setTextColor(60, 60, 60);
      doc.text(bold, col - fw / 2, 44);
      doc
        .setFont("helvetica", "normal")
        .text(val, col - fw / 2 + doc.getTextWidth(bold), 44);
    });

    const rows = sortedData.map((item) => [
      item.Designation || "",
      item.Strength,
      item.Attendance,
      Number(item.Strength) - Number(item.Attendance),
      `${Math.round(Number(item.Percentage || 0))}%`,
    ]);
    rows.push([
      "Total",
      totalStrength,
      totalAttendance,
      totalAbsent,
      `${totalPercentage}%`,
    ]);

    autoTable(doc, {
      startY: 52,
      head: [["Designation", "Strength", "Attendance", "Absent", "%"]],
      body: rows,
      styles: { fontSize: 11, cellPadding: 4 },
      headStyles: {
        fillColor: [0, 74, 173],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: { fillColor: [248, 250, 255] },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
      },
      theme: "grid",
      didParseCell(data) {
        if (data.row.index === rows.length - 1) {
          data.cell.styles.fillColor = [0, 74, 173];
          data.cell.styles.textColor = 255;
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage(data) {
        doc.setFontSize(8).setTextColor(150).setFont("helvetica", "italic");
        doc.text(
          `Generated on ${formatDate(at)} at ${formatTime(at)}`,
          pw / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: "center" }
        );
        doc.text(
          `Page ${data.pageNumber}`,
          pw - 20,
          doc.internal.pageSize.getHeight() - 10
        );
      },
    });

    doc.save(`DesignationAttendance_${formatDate(selectedDate)}.pdf`);
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Page>
        {/* ── Filter row ── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
          <Box
            onClick={() => setOpenDatePicker(true)}
            sx={{
              height: 36,
              borderRadius: "20px",
              border: "0.5px solid #e2e8f0",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              px: "12px",
              cursor: "pointer",
              flexShrink: 0,
              "&:hover": { borderColor: "#330066" },
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography sx={{ fontSize: 12, color: "#1e293b", fontWeight: 500 }}>
              {formatDate(selectedDate)}
            </Typography>
          </Box>

          {openDatePicker && (
            <DatePicker
              open={openDatePicker}
              value={selectedDate}
              onChange={handleDateChange}
              onClose={() => setOpenDatePicker(false)}
              renderInput={(params) => (
                <TextField {...params} sx={{ display: "none" }} />
              )}
            />
          )}

          <Box sx={{ flex: 1 }} />

          {/* PDF export button */}
          {!loading && sortedData.length > 0 && (
            <Button
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
              onClick={handleExportPDF}
              sx={{
                height: 36,
                borderRadius: "20px",
                background: "#16a34a",
                color: "#fff",
                fontSize: 12,
                fontWeight: 500,
                textTransform: "none",
                px: 1.5,
                flexShrink: 0,
                "&:hover": { background: "#15803d" },
              }}
            >
              PDF
            </Button>
          )}
        </Box>

        {/* ── Stats row ── */}
        {!loading && !error && sortedData.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <StatCard label="Strength" value={totalStrength} color="#330066" />
            <StatCard label="Present" value={totalAttendance} color="#16a34a" />
            <StatCard label="Absent" value={totalAbsent} color="#dc2626" />
            <Box
              sx={{
                flex: "1 1 0",
                background: "#fff",
                border: "0.5px solid #e2e8f0",
                borderRadius: "12px",
                p: "10px 12px",
                minWidth: 0,
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#64748b", mb: 0.5 }}>
                Att %
              </Typography>
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 500,
                  color: rateColor(totalPercentage),
                  lineHeight: 1.1,
                }}
              >
                {totalPercentage}%
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Body states ── */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: "#330066" }} />
          </Box>
        ) : error ? (
          /* ── Error state ── */
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 2,
              background: "#fff",
              border: "0.5px solid #fecaca",
              borderRadius: 12,
            }}
          >
            <Typography sx={{ fontSize: 13, color: "#dc2626", mb: 1, fontWeight: 600 }}>
              Failed to load data
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: "#94a3b8",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              {error}
            </Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => load(selectedDate)}
              sx={{
                height: 32,
                borderRadius: "20px",
                background: "#330066",
                color: "#fff",
                fontSize: 12,
                fontWeight: 500,
                textTransform: "none",
                px: 2,
                "&:hover": { background: "#1e003d" },
              }}
            >
              Retry
            </Button>
          </Box>
        ) : sortedData.length > 0 ? (
          <>
            <SectionLabel>
              Designation Summary ({sortedData.length} designations)
            </SectionLabel>
            <Card>
              <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                <TableHead>
                  <TableRow>
                    {[
                      { label: "Designation", w: "36%" },
                      { label: "Strength", w: "16%" },
                      { label: "Present", w: "16%" },
                      { label: "Absent", w: "16%" },
                      { label: "%", w: "16%" },
                    ].map((h) => (
                      <TableCell
                        key={h.label}
                        sx={{
                          width: h.w,
                          background: "#330066",
                          color: "#fff",
                          fontSize: isMobile ? 10 : 11,
                          fontWeight: 600,
                          py: "8px",
                          px: "10px",
                          borderBottom: "none",
                          textAlign: h.label !== "Designation" ? "center" : "left",
                        }}
                      >
                        {h.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((row, i) => {
                    const strength = Number(row.Strength || 0);
                    const attendance = Number(row.Attendance || 0);
                    const absent = strength - attendance;
                    const rate = Math.round(Number(row.Percentage || 0));
                    return (
                      <TableRow
                        key={i}
                        sx={{
                          "&:nth-of-type(even) td": { background: "#f8faff" },
                          "&:hover td": { background: "rgba(0,74,173,0.04)" },
                          "&:last-child td": { borderBottom: "none" },
                        }}
                      >
                        <TableCell sx={{ px: "10px", py: "8px" }}>
                          <Typography
                            sx={{
                              fontSize: isMobile ? 12 : 13,
                              fontWeight: 600,
                              color: "#1e293b",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.Designation}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              px: "8px",
                              py: "2px",
                              borderRadius: "10px",
                              fontSize: 11,
                              fontWeight: 600,
                              background: "#e8f0fe",
                              color: "#330066",
                            }}
                          >
                            {strength}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              px: "8px",
                              py: "2px",
                              borderRadius: "10px",
                              fontSize: 11,
                              fontWeight: 600,
                              background: "#dcfce7",
                              color: "#16a34a",
                            }}
                          >
                            {attendance}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              px: "8px",
                              py: "2px",
                              borderRadius: "10px",
                              fontSize: 11,
                              fontWeight: 600,
                              background: absent > 0 ? "#fee2e2" : "#f1f5f9",
                              color: absent > 0 ? "#dc2626" : "#64748b",
                            }}
                          >
                            {absent}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                          <PctBadge pct={rate} />
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* ── Total row ── */}
                  <TableRow
                    sx={{
                      "& td": {
                        background: "#EFF4FF !important",
                        borderBottom: "none",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        px: "10px",
                        py: "8px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#330066",
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          px: "8px",
                          py: "2px",
                          borderRadius: "10px",
                          fontSize: 11,
                          fontWeight: 600,
                          background: "#fff",
                          color: "#330066",
                          border: "0.5px solid #c7d7f8",
                        }}
                      >
                        {totalStrength}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          px: "8px",
                          py: "2px",
                          borderRadius: "10px",
                          fontSize: 11,
                          fontWeight: 600,
                          background: "#fff",
                          color: "#16a34a",
                          border: "0.5px solid #bbf7d0",
                        }}
                      >
                        {totalAttendance}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          px: "8px",
                          py: "2px",
                          borderRadius: "10px",
                          fontSize: 11,
                          fontWeight: 600,
                          background: "#fff",
                          color: totalAbsent > 0 ? "#dc2626" : "#64748b",
                          border: `0.5px solid ${totalAbsent > 0 ? "#fecaca" : "#e2e8f0"}`,
                        }}
                      >
                        {totalAbsent}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", px: "6px", py: "8px" }}>
                      <PctBadge pct={totalPercentage} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography sx={{ fontSize: 14, color: "#94a3b8" }}>
              No designation data found for {formatDate(selectedDate)}
            </Typography>
          </Box>
        )}
      </Page>
    </LocalizationProvider>
  );
}